import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { productGradeApi, F118 } from '../../../services/productGradeApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useProductGradeForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F118[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productGradeApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setCode(current.f118_01);
      setDescription(current.f118_11);
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setCode('');
    setDescription('');
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setCode(current.f118_01);
    setDescription(current.f118_11);
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setCode(current.f118_01);
      setDescription(current.f118_11);
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await productGradeApi.create({ f118_01: code, f118_11: description });
        message.success('新增成功');
      } else {
        await productGradeApi.update(current!.f118_01, { f118_11: description });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = mode === 'add' ? code : current!.f118_01;
      const idx = data.findIndex(r => r.f118_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await productGradeApi.delete(current.f118_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Copy
  const doCopy = async (newCode: string) => {
    if (!current) return;
    try {
      await productGradeApi.copy(current.f118_01, { f118_01: newCode, f118_11: current.f118_11 });
      message.success('複製成功');
      const data = await loadRecords();
      const idx = data.findIndex(r => r.f118_01 === newCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error('複製失敗');
      }
    }
  };

  // Quick search
  const quickSearch = (searchCode: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f118_01 >= searchCode);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode, description, setDescription,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, doCopy, quickSearch,
    loadRecords,
  };
}
